import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectors as SectionSelectors } from '@reducers/sections';
import { useSearchParams } from '@utils/hooks/useSearchParams';
import BellringerContextDialog, {
  BELLRINGER_STAY_OPTION,
} from '@components/Component/OLP/tab/ActivityFactory/BellringerContextDialog';
import { useLaunchBellringerSession } from '@components/Component/OLP/tab/ActivityFactory/useLaunchBellringerSession';
import { useBellringerInactivity } from './useBellringerInactivity';

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;

interface Section {
  id: number;
  name: string;
  course: number | string;
}

interface BellringerInactivityNudgeProps {
  /** Run the timer only in an active bellringer session. */
  active: boolean;
  /** Overridable for tests. */
  timeoutMs?: number;
}

/**
 * Watches for inactivity during a bellringer session and, on timeout, opens the
 * (unskippable) "still with this class?" nudge. "I'm still working on it" closes
 * it (no change). Choosing another section switches: it resolves that section's
 * own group activity, resets context, emits a fresh `bellringer_launched`, and
 * re-navigates — all via the shared `useLaunchBellringerSession`.
 */
const BellringerInactivityNudge = ({
  active,
  timeoutMs = FIFTEEN_MINUTES_MS,
}: BellringerInactivityNudgeProps): React.ReactElement | null => {
  const [open, setOpen] = useState(false);
  const { courseId, sectionId } = useParams<{ courseId: string; sectionId: string }>();
  const { activityLessonId } = useSearchParams() as { activityLessonId?: string };
  const allSections = useSelector(SectionSelectors.getSectionList) as Section[];
  const launchBellringerSession = useLaunchBellringerSession();

  const currentSection = (allSections || []).find(
    (section) => String(section.id) === String(sectionId),
  );
  const otherSections = (allSections || [])
    .filter(
      (section) =>
        String(section.course) === String(courseId) &&
        String(section.id) !== String(sectionId),
    )
    .map((section) => ({ id: section.id, name: section.name }));

  useBellringerInactivity({
    enabled: active && !open,
    timeoutMs,
    onTimeout: () => setOpen(true),
  });

  // Never leave a stale nudge open if we drop out of bellringer mode.
  useEffect(() => {
    if (!active) {
      setOpen(false);
    }
  }, [active]);

  const handleStay = () => setOpen(false);

  const handleContinue = async ({
    intent,
    sectionId: targetSectionId,
  }: {
    intent: 'teaching' | 'preview' | 'stay';
    sectionId: number | null;
  }) => {
    // Close immediately (before the async launch) so the unskippable dialog cannot
    // be double-submitted while the request is in flight.
    setOpen(false);
    if (intent !== 'teaching' || targetSectionId == null) {
      // "I'm still working on it" — no change, resume the timer.
      return;
    }
    await launchBellringerSession({ sectionId: targetSectionId, activityLessonId });
  };

  if (!active) {
    return null;
  }

  const title = currentSection
    ? `Are you still with ${currentSection.name}?`
    : 'Are you still with this class?';

  return (
    <BellringerContextDialog
      open={open}
      title={title}
      sections={otherSections}
      firstOption={{
        label: "I'm still working on it",
        value: BELLRINGER_STAY_OPTION,
        intent: 'stay',
      }}
      showCancel={false}
      onCancel={handleStay}
      onContinue={handleContinue}
    />
  );
};

export default BellringerInactivityNudge;
